// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { Box, useTheme } from '@mui/system';
import { Select, MenuItem, InputBase, Grid, IconButton } from '@mui/material';
import gridviewIcon from 'Assets/grid-view-icon.png';
import gridviewIconActive from 'Assets/grid-view-icon-ACTIVE.png';
import mapviewIcon from 'Assets/map-view-icon.png';
import mapviewIconActive from 'Assets/map-view-icon-ACTIVE.png';
import stylesDef from './styles';
import { MarkOrderSortFilterTouchedAction, SetKeywordFilterAction, SetLoadAllAction, SetLoadNewAction, SetOrderSortFilterAction, SetPageAction, SetProjectsAction, SetSortFilterAction } from '../projectListActions';
import { GetProjectsThunk } from '../projectListThunk';
import SearchField from 'Components/SearchField';
import { SetFoldersAction } from 'Features/folderList/folderListActions';
import { selectEffectiveOrderSortFilter } from '../projectListSelector';

type PageToolbarProps = {
  showMapview: boolean;
  showMapviewClick: () => void;
  showGridviewClick: () => void;
};

// Single source of truth for the filter dropdown's menu items.
const ORDER_SORT_FILTER_OPTIONS: { value: ProjectOrderSortFilter; label: string }[] = [
  { value: 'all', label: 'All Projects' },
  { value: 'unassigned', label: 'Unassigned Projects' },
  { value: 'assigned', label: 'Assigned Projects' },
  { value: 'estimate', label: 'Estimate' },
  { value: 'completed', label: 'Completed' },
  { value: 'canceled', label: 'Canceled' },
];

const PageToolbar = ({ showMapview, showMapviewClick, showGridviewClick }: PageToolbarProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme, showMapview);
  const dispatch = useAppDispatch();
  const toolbarStyle: {} = showMapview ? styles.pageToolbarMapview : styles.pageToolbar;
  const toolbarContainer: {} = showMapview ? styles.pageToolbarContainerMapview : styles.pageToolbarContainer;
  const { sortFilter, keywordFilter } = useAppSelector(
    (state) => state.projectList,
  );
  const effectiveOrderSortFilter = useAppSelector(selectEffectiveOrderSortFilter);

  // Mark the filter touched the instant the dropdown opens, not just on a change. MUI's Select
  // skips onChange when the clicked item's value equals the currently-bound one - so if we only
  // marked touched in onChange, whichever option happens to match the untouched default's real
  // value (e.g. clicking "Unassigned Projects" while it's already the untouched raw value, even
  // though the closed box reads "All Projects" via the override below) would silently do nothing,
  // no matter which value we bind. Marking touched on open resolves the override immediately, so
  // by the time the menu is interactive, the bound value and every item's value already agree -
  // there's no state left where a real click can be swallowed as a no-op.
  const handleOrderSortFilterOpen = () => {
    dispatch(MarkOrderSortFilterTouchedAction());
  };

  const loadNewData = () => {
    dispatch(SetLoadNewAction(true));
    dispatch(SetLoadAllAction(false));
    dispatch(SetPageAction(0));
    dispatch(SetProjectsAction([]));
    dispatch(SetFoldersAction([]));
    dispatch(GetProjectsThunk());
  };

  return (
    <Box sx={toolbarStyle}>
      <Grid sx={toolbarContainer}>
        <SearchField
          value={keywordFilter}
          onChange={(e) => {
            dispatch(SetKeywordFilterAction(e));
            loadNewData();
          }}
        />
        <IconButton
          aria-label="Mapview"
          onClick={showMapviewClick}
          // target="_blank"
          sx={styles.icons}
        >
          {showMapview ? (
            <img src={mapviewIconActive} alt="Gridview" />
          ) : (
            <img src={mapviewIcon} alt="Gridview" />
          )}
        </IconButton>
        <IconButton
          aria-label="Gridview"
          onClick={showGridviewClick}
          // target="_blank"
          sx={styles.icons}
        >
          {showMapview ? (
            <img src={gridviewIcon} alt="Gridview" />
          ) : (
            <img src={gridviewIconActive} alt="Gridview" />
          )}
        </IconButton>
        <Select
          inputProps={{
            name: 'sort',
            id: 'sort',
          }}
          input={<InputBase />}
          value={sortFilter}
          onChange={(ev) => {
            dispatch(SetSortFilterAction(ev.target.value));
            loadNewData();
          }}
        >
          <MenuItem sx={styles.fontWeight} value="updatedAtD">
            Recent
          </MenuItem>
          <MenuItem sx={styles.fontWeight} value="nameA">
            A-Z
          </MenuItem>
          <MenuItem sx={styles.fontWeight} value="nameD">
            Z-A
          </MenuItem>
        </Select>
        <Select
          inputProps={{
            name: 'filterby',
            id: 'filterby',
          }}
          input={<InputBase />}
          value={effectiveOrderSortFilter}
          onOpen={handleOrderSortFilterOpen}
          onChange={(ev) => {
            dispatch(SetOrderSortFilterAction(ev.target.value));
            dispatch(MarkOrderSortFilterTouchedAction());
            loadNewData();
          }}
        >
          {ORDER_SORT_FILTER_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
    </Box>
  );
};

export default PageToolbar;
